/**
 * The order lifecycle, as the screen offers it.
 *
 * The rule itself lives on the server (`orders.services.TRANSITIONS`) and the server enforces it;
 * this copy only decides which buttons to show, so a move the server would refuse is never
 * offered. Refunded is left out: an order becomes refunded by recording the refund, which has its
 * own form.
 */

import type { components } from "@/api/schema";

export type OrderStatus = components["schemas"]["OrderStatus"];

export const STATUSES: OrderStatus[] = [
  "draft",
  "confirmed",
  "in_progress",
  "completed",
  "cancelled",
  "refunded",
];

const NEXT: Record<OrderStatus, OrderStatus[]> = {
  draft: ["confirmed", "cancelled"],
  confirmed: ["in_progress", "cancelled"],
  in_progress: ["completed", "cancelled"],
  completed: [],
  cancelled: [],
  refunded: [],
};

/** The status changes to offer as buttons, the way forward first. */
export function nextStatuses(status: OrderStatus): OrderStatus[] {
  return NEXT[status];
}

/** Whether the order's details and items may still change, as on the server. */
export function isEditable(status: OrderStatus): boolean {
  return status === "draft" || status === "confirmed" || status === "in_progress";
}

/** Whether the order takes money in: not once it is over. */
export function takesPayments(status: OrderStatus): boolean {
  return status !== "cancelled" && status !== "refunded";
}

interface PaymentRow {
  id: string;
  direction: string;
}

/** Whether to offer removing a payment. A refunded order keeps its last money out, the refund,
 * as the server does (`payments.services.delete_payment`): it cannot be reopened, so removing the
 * refund would leave it refunded with nothing given back. */
export function canRemovePayment(
  status: OrderStatus,
  payment: PaymentRow,
  payments: PaymentRow[],
): boolean {
  if (status !== "refunded" || payment.direction !== "out") return true;
  return payments.some((other) => other.direction === "out" && other.id !== payment.id);
}
